'use client';
import { AlertTriangle, Brain, CheckCircle2, FileEdit, FolderOpen, HardDrive, Home, ImageIcon, MessageSquare, Palette, Ruler, Search, XCircle } from 'lucide-react';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTheme } from '@/components/ThemeContext';
import { getThemeStyles } from '@/lib/themeStyles';

const STYLES = [
 { key: 'photorealistic', label: ' 포토리얼', desc: 'V-Ray급 실사' },
 { key: 'modern', label: ' 모던', desc: '미니멀·글래스' },
 { key: 'luxury', label: ' 럭셔리', desc: '대리석·브라스' },
 { key: 'warm', label: ' 웜', desc: '우드·내추럴' },
 { key: 'scandinavian', label: ' 스칸디', desc: '화이트·라이트우드' },
 { key: 'industrial', label: ' 인더스트리얼', desc: '벽돌·스틸' },
];

const ENGINES = [
 { key: 'nanobanana', label: ' Nano Banana 2', desc: '초고속 클라우드', icon: '☁️' },
 { key: 'flux', label: ' FLUX.1 Edge', desc: '최상위 로컬가속', icon: '⚡' }
];

const QUALITIES = [
 { key: 'draft', label: ' Draft' },
 { key: 'high', label: ' High' },
 { key: '4K', label: ' 4K' },
];

interface NasFile {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size?: number;
  isImage?: boolean;
}

export default function RenAgentPage() {
  const [mode, setMode] = useState<'upload' | 'nas'>('upload');
  const [engine, setEngine] = useState('nanobanana');
  const [style, setStyle] = useState('photorealistic');
  const [quality, setQuality] = useState('high');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 이미지 상태
  const [inputPreview, setInputPreview] = useState<string | null>(null);
  const [inputBase64, setInputBase64] = useState<string | null>(null);
  const [inputMime, setInputMime] = useState('image/png');

  // 결과
  const [result, setResult] = useState<any>(null);

  // NAS 브라우징
  const [nasPath, setNasPath] = useState('B_DESIGN');
  const [nasFiles, setNasFiles] = useState<NasFile[]>([]);
  const [nasLoading, setNasLoading] = useState(false);
  const [selectedNasPath, setSelectedNasPath] = useState('');

  const imgRef = useRef<HTMLInputElement>(null);

  // ── NAS 디렉토리 탐색 ──
  const browseNas = useCallback(async (path: string) => {
    setNasLoading(true);
    setNasPath(path);
    try {
      const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenantId') || 'desinsight' : 'desinsight';
      const res = await fetch(`/api/webdav/ls?dir=${encodeURIComponent(path)}`, {
        headers: { 'x-tenant-id': tenantId }
      });
      const data = await res.json();
      const items = data.items || data.files || data;
      const arr = Array.isArray(items) ? items : [];
      const files: NasFile[] = arr.map((item: any) => {
        const name = item.name || item.filename || item.basename || '';
        const ext = name.split('.').pop()?.toLowerCase() || '';
        const isImage = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'tiff', 'psd', 'gif', 'svg'].includes(ext);
        return {
          name,
          path: item.path || `${path}/${name}`,
          type: item.type === 'directory' || item.isDirectory || item.type === 'd' || item.type === 'dir' ? 'directory' as const : 'file' as const,
          size: item.size ? parseInt(item.size) : undefined,
          isImage,
        };
      });
      files.sort((a, b) => {
        if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
        if (a.isImage !== b.isImage) return a.isImage ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      setNasFiles(files);
    } catch (e: any) {
      setError(`NAS 탐색 실패: ${e.message}`);
    } finally {
      setNasLoading(false);
    }
  }, []);

  // NAS 이미지 선택
  const selectNasFile = useCallback((file: NasFile) => {
    if (file.type === 'directory') {
      browseNas(file.path);
    } else if (file.isImage) {
      setSelectedNasPath(file.path);
      setInputPreview(null);
      setInputBase64(null);
    }
  }, [browseNas]);

  // 상위 폴더
  const goUp = useCallback(() => {
    const parts = nasPath.split('/');
    if (parts.length > 1) {
      parts.pop();
      browseNas(parts.join('/'));
    }
  }, [nasPath, browseNas]);

  // ── 이미지 업로드 (자동 리사이즈: Vercel 4.5MB Payload Limit 방어) ──
  const handleUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const MAX_DIM = 1600;
        
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.85); // 용량 최적화 (JPEG 85%)
          
          setInputPreview(resizedDataUrl);
          const match = resizedDataUrl.match(/^data:(image\/[^;]+);base64,(.+)$/);
          if (match) {
            setInputMime(match[1]);
            setInputBase64(match[2]);
          }
          setSelectedNasPath('');
        }
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }, []);

  // ── 렌더링 실행 ──
  const startRender = useCallback(async () => {
    if (!inputBase64 && !selectedNasPath) {
      return setError('스케치업 이미지를 업로드하거나 NAS에서 선택하세요');
    }
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const bodyData: any = { engine, style, quality, prompt };
      if (inputBase64) {
        bodyData.imageBase64 = inputBase64;
        bodyData.imageMimeType = inputMime;
      } else if (selectedNasPath) {
        bodyData.nasPath = selectedNasPath;
      }

      const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenantId') || 'desinsight' : 'desinsight';
      const res = await fetch('/api/ren-agent/render', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-tenant-id': tenantId
        },
        body: JSON.stringify(bodyData),
      });

      const data = await res.json();
      if (data.success) {
        setResult(data);
      } else {
        setError(data.error || '렌더링 실패');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [engine, inputBase64, inputMime, selectedNasPath, style, quality, prompt]);

  // ── 결과 이미지 다운로드 (Blob 방식으로 용량 제한 우회) ──
  const downloadResult = useCallback(() => {
    if (!result?.renderedImage) return;
    try {
      // Base64 포맷 정리 (data: URI 접두사 제거나 공백 제거)
      let base64Clean = result.renderedImage.replace(/\s+/g, '');
      if (base64Clean.startsWith('data:')) {
        base64Clean = base64Clean.split(',')[1] || base64Clean;
      }

      const byteString = atob(base64Clean);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: result.renderedMimeType || 'image/png' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `render_${style}_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch(e: any) {
      console.error('Blob Download failed', e);
      // Fallback
      if (window.confirm('Blob 변환 실패. 강제로 다운로드를 시도합니다. (용량에 따라 브라우저가 차단할 수 있습니다)')) {
        const link = document.createElement('a');
        link.href = result.renderedImage.startsWith('data:') 
                      ? result.renderedImage 
                      : `data:${result.renderedMimeType || 'image/png'};base64,${result.renderedImage}`;
        link.download = `render_${style}_${Date.now()}.png`;
        link.click();
      }
    }
  }, [result, style]);

  // 초기 NAS 로드
  useEffect(() => {
    if (mode === 'nas' && nasFiles.length === 0) browseNas(nasPath);
  }, [mode]);

  const fmt = (n: number) => {
    if (n > 1048576) return `${(n / 1048576).toFixed(1)}MB`;
    if (n > 1024) return `${Math.round(n / 1024)}KB`;
    return `${n}B`;
  };

  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const t = getThemeStyles(isDark);

  return (
    <div style={{ minHeight: '100vh', paddingTop: 64, background: isDark ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1025 30%, #0f1729 70%, #0a0a1a 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #faf5ff 30%, #f1f5f9 70%, #f8fafc 100%)', color: t.textLight, fontFamily: "'Inter', 'Pretendard', sans-serif" }}>
      {/* ── 헤더 ── */}
      <header style={{ borderBottom: `1px solid ${isDark ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.12)'}`, padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
 <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #8b5cf6, #a855f7, #c084fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}><Palette className="w-4 h-4 inline-block" /></div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, background: 'linear-gradient(135deg, #c084fc, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Rendering Agent
            </h1>
            <p style={{ fontSize: 11, color: isDark ? '#7c3aed' : '#6d28d9', margin: 0 }}>SketchUp → Photorealistic · Nano Banana 2 & FLUX.1</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[{ href: '/skp-agent', label: 'SKP Agent' }, { href: '/', label: 'Home' }].map(l => (
            <a key={l.href} href={l.href} style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.15)'}`, color: isDark ? '#a78bfa' : '#7c3aed', fontSize: 12, textDecoration: 'none' }}>{l.label}</a>
          ))}
        </div>
      </header>

      <main style={{ maxWidth: 1440, margin: '0 auto', padding: 24, display: 'grid', gridTemplateColumns: '320px 1fr', gap: 24 }}>
        {/* ── 좌측: 설정 패널 ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 모드 전환 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, padding: 4, borderRadius: 10, background: 'rgba(255,255,255,0.03)' }}>
 {[{ key: 'upload' as const, label: '이미지 업로드' }, { key: 'nas' as const, label: 'NAS 탐색' }].map(m =>(
              <button key={m.key} onClick={() => setMode(m.key)}
                style={{ padding: '10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: mode === m.key ? 600 : 400,
                  background: mode === m.key ? 'rgba(139,92,246,0.2)' : 'transparent', color: mode === m.key ? '#c084fc' : '#64748b' }}>
                {m.label}
              </button>
            ))}
          </div>

          {/* 이미지 업로드 */}
          {mode === 'upload' && (
            <div style={{ borderRadius: 12, border: '2px dashed rgba(139,92,246,0.3)', background: 'rgba(139,92,246,0.03)', padding: 20, textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s' }}
              onClick={() => imgRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#8b5cf6'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = 'rgba(139,92,246,0.3)'; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'rgba(139,92,246,0.3)'; if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); }}>
              <input ref={imgRef} type="file" accept="image/*" hidden onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])} />
              {inputPreview ? (
                <img src={inputPreview} alt="Input" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'contain' }} />
              ) : (
                <>
 <div style={{ fontSize: 32, marginBottom: 8 }}><Home className="w-4 h-4 inline-block" /></div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#c084fc' }}>스케치업 이미지 업로드</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>JPG/PNG · 클릭 또는 드래그</div>
                </>
              )}
            </div>
          )}

          {/* NAS 브라우저 */}
          {mode === 'nas' && (
            <div style={{ borderRadius: 12, border: '1px solid rgba(139,92,246,0.15)', background: 'rgba(255,255,255,0.02)', overflow: 'hidden' }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={goUp} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#a78bfa', fontSize: 11, cursor: 'pointer' }}>⬆️ 상위</button>
 <span style={{ fontSize: 10, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}><FolderOpen className="w-4 h-4 inline-block" />{nasPath}</span>
                {nasLoading && <span style={{ fontSize: 10, color: '#c084fc' }}>...</span>}
              </div>
              <div style={{ maxHeight: 280, overflow: 'auto' }}>
                {nasFiles.map((f, i) => (
                  <div key={i} onClick={() => selectNasFile(f)}
                    style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11,
                      borderBottom: '1px solid rgba(255,255,255,0.03)',
                      background: f.path === selectedNasPath ? 'rgba(139,92,246,0.15)' : 'transparent',
                      color: f.type === 'directory' ? '#a78bfa' : f.isImage ? '#4ade80' : '#64748b' }}>
 <span>{f.type === 'directory' ? '' : f.isImage ? '' : ''}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    {f.size && <span style={{ fontSize: 9, color: '#475569' }}>{fmt(f.size)}</span>}
                  </div>
                ))}
                {nasFiles.length === 0 && !nasLoading && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#475569', fontSize: 11 }}>폴더가 비어있습니다</div>
                )}
              </div>
              {selectedNasPath && (
                <div style={{ padding: '8px 12px', borderTop: '1px solid rgba(139,92,246,0.15)', background: 'rgba(139,92,246,0.05)', fontSize: 10, color: '#c084fc' }}>
 <CheckCircle2 className="w-4 h-4 inline-block" />선택: {selectedNasPath.split('/').pop()}
                </div>
              )}
            </div>
          )}

          {/* 엔진 선택 */}
          <div>
 <div style={{ fontSize: 11, fontWeight: 600, color: '#a78bfa', marginBottom: 8 }}><Brain className="w-4 h-4 inline-block" />AI 렌더링 엔진</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {ENGINES.map(e => (
                <button key={e.key} onClick={() => setEngine(e.key)}
                  style={{ padding: '10px 8px', borderRadius: 8, border: engine === e.key ? '1px solid rgba(139,92,246,0.6)' : '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', textAlign: 'left',
                    background: engine === e.key ? 'rgba(139,92,246,0.15)' : 'rgba(255,255,255,0.02)', transition: 'all 0.15s',
                    boxShadow: engine === e.key && e.key === 'flux' ? '0 0 10px rgba(139,92,246,0.2)' : 'none' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: engine === e.key ? '#fff' : '#94a3b8' }}>
                    {e.icon} {e.label}
                  </div>
                  <div style={{ fontSize: 9, color: engine === e.key ? '#c084fc' : '#64748b', marginTop: 3 }}>{e.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* 렌더링 스타일 */}
          <div>
 <div style={{ fontSize: 11, fontWeight: 600, color: '#a78bfa', marginBottom: 8 }}><Palette className="w-4 h-4 inline-block" />렌더링 스타일</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {STYLES.map(s => (
                <button key={s.key} onClick={() => setStyle(s.key)}
                  style={{ padding: '10px 8px', borderRadius: 8, border: style === s.key ? '1px solid rgba(139,92,246,0.5)' : '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', textAlign: 'left',
                    background: style === s.key ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.02)', transition: 'all 0.15s' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: style === s.key ? '#c084fc' : '#94a3b8' }}>{s.label}</div>
                  <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* 품질 */}
          <div>
 <div style={{ fontSize: 11, fontWeight: 600, color: '#a78bfa', marginBottom: 8 }}><ImageIcon className="w-4 h-4 inline-block" />렌더 품질</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {QUALITIES.map(q => (
                <button key={q.key} onClick={() => setQuality(q.key)}
                  style={{ padding: '8px', borderRadius: 8, border: quality === q.key ? '1px solid rgba(139,92,246,0.5)' : '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', fontSize: 12, fontWeight: 600, textAlign: 'center',
                    background: quality === q.key ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.02)', color: quality === q.key ? '#c084fc' : '#64748b' }}>
                  {q.label}
                </button>
              ))}
            </div>
          </div>

          {/* 추가 프롬프트 */}
          <div>
 <div style={{ fontSize: 11, fontWeight: 600, color: '#a78bfa', marginBottom: 8 }}><MessageSquare className="w-4 h-4 inline-block" />추가 지시 (선택)</div>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3} placeholder="예: 골든아워 자연광, 사람 포함, 큰 창문에 커튼 추가..."
              style={{ width: '100%', borderRadius: 8, border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', color: isDark ? '#e2e8f0' : '#1e293b', fontSize: 12, padding: '10px 12px', resize: 'vertical', outline: 'none' }} />
          </div>

          {/* 실행 버튼 */}
          <button onClick={startRender} disabled={loading || (!inputBase64 && !selectedNasPath)}
            style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 700,
              background: loading ? 'rgba(139,92,246,0.2)' : engine === 'flux' ? 'linear-gradient(135deg, #6366f1, #8b5cf6, #3b82f6)' : 'linear-gradient(135deg, #8b5cf6, #a855f7, #7c3aed)',
              color: '#fff', opacity: (!inputBase64 && !selectedNasPath) ? 0.4 : 1, transition: 'all 0.2s',
              boxShadow: loading ? 'none' : '0 4px 20px rgba(139,92,246,0.3)' }}>
 {loading ? (engine === 'flux' ? 'FLUX.1 로컬 가속 생성 중...' : 'Nano Banana 2 생성 중...') : '실사 렌더링 생성'}
          </button>

          {error && (
            <div style={{ padding: '12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5', fontSize: 12 }}>
 <XCircle className="w-4 h-4 inline-block" />{error}
            </div>
          )}

          {/* 모델 정보 */}
          <div style={{ padding: 14, borderRadius: 10, background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.12)', fontSize: 10, color: '#7c3aed', lineHeight: 1.8 }}>
 <strong><Brain className="w-4 h-4 inline-block" />AI 엔진</strong><br />
            분석: Gemini 2.5 Flash (Vision)<br />
            생성: <strong>{engine === 'flux' ? 'FLUX.1 [schnell] (MPS Local 가속)' : 'Nano Banana 2 (Cloud)'}</strong><br />
            NAS: grid.snapcodex.com (Cloudflare 터널)
          </div>
        </div>

        {/* ── 우측: 결과 ── */}
        <div>
          {loading && (
            <div style={{ height: '100%', minHeight: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 16, border: '1px solid rgba(139,92,246,0.15)', background: 'rgba(139,92,246,0.03)' }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', border: '4px solid rgba(139,92,246,0.2)', borderTopColor: engine === 'flux' ? '#3b82f6' : '#8b5cf6', animation: 'spin 1s linear infinite' }} />
              <div style={{ marginTop: 20, fontSize: 16, fontWeight: 600, color: '#c084fc' }}>{engine === 'flux' ? 'FLUX.1 Edge Processing...' : 'Nano Banana 2 렌더링 중...'}</div>
              <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 8 }}>{engine === 'flux' ? '로컬 GPU(MPS) 연산 가속 중 · 극사실주의 활성화' : 'Vision 분석 → 스타일 매핑 → 실사 생성'}</div>
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          )}

          {result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Before/After */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Before */}
                <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
 <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)', fontSize: 11, fontWeight: 600, color: '#94a3b8' }}><Ruler className="w-4 h-4 inline-block" />INPUT — SketchUp</div>
                  {inputPreview ? (
                    <img src={inputPreview} alt="Input" style={{ width: '100%', aspectRatio: '16/10', objectFit: 'contain', background: '#0a0a0a' }} />
                  ) : selectedNasPath ? (
                    <div style={{ padding: 40, textAlign: 'center', background: '#0a0a0a', aspectRatio: '16/10', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
 <div style={{ fontSize: 32 }}><FolderOpen className="w-4 h-4 inline-block" /></div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>{selectedNasPath.split('/').pop()}</div>
                    </div>
                  ) : null}
                </div>

                {/* After */}
                <div style={{ borderRadius: 16, overflow: 'hidden', border: result.hasImage ? '1px solid rgba(139,92,246,0.3)' : '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ padding: '8px 12px', background: result.hasImage ? 'rgba(139,92,246,0.1)' : 'rgba(255,255,255,0.04)', fontSize: 11, fontWeight: 600, color: result.hasImage ? '#c084fc' : '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 <span><Palette className="w-4 h-4 inline-block" />OUTPUT — {result.engine === 'flux' ? 'FLUX.1 [schnell]' : 'Nano Banana 2'}</span>
                    {result.hasImage && (
                      <button onClick={downloadResult} style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(139,92,246,0.1)', color: '#c084fc', fontSize: 10, cursor: 'pointer' }}>
 <HardDrive className="w-4 h-4 inline-block" />저장
                      </button>
                    )}
                  </div>
                  {result.hasImage ? (
                    <img src={`data:${result.renderedMimeType};base64,${result.renderedImage}`} alt="Rendered" style={{ width: '100%', aspectRatio: '16/10', objectFit: 'contain', background: '#0a0a0a' }} />
                  ) : (
                    <div style={{ padding: 40, textAlign: 'center', background: '#0a0a0a', aspectRatio: '16/10', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
 <div style={{ fontSize: 32 }}><AlertTriangle className="w-4 h-4 inline-block" /></div>
                      <div style={{ fontSize: 12, color: '#fbbf24', marginTop: 8 }}>이미지 생성 불가</div>
                      <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>프롬프트/분석 결과만 반환됨</div>
                    </div>
                  )}
                </div>
              </div>

              {/* 분석 결과 */}
              {result.analysis && Object.keys(result.analysis).length > 0 && (
                <div style={{ borderRadius: 12, border: '1px solid rgba(139,92,246,0.12)', background: 'rgba(139,92,246,0.03)', padding: 16 }}>
 <div style={{ fontSize: 13, fontWeight: 600, color: '#c084fc', marginBottom: 12 }}><Search className="w-4 h-4 inline-block" />Vision AI 분석 결과</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                    {result.analysis.spaceType && (
                      <InfoCard label="공간 유형" value={result.analysis.spaceType} />
                    )}
                    {result.analysis.viewType && (
                      <InfoCard label="뷰 타입" value={result.analysis.viewType} />
                    )}
                    {result.analysis.mood && (
                      <InfoCard label="분위기" value={result.analysis.mood} />
                    )}
                    {result.analysis.materials && (
                      <InfoCard label="재질" value={result.analysis.materials.join(', ')} />
                    )}
                  </div>
                </div>
              )}

              {/* 프롬프트 */}
              {result.prompt && (
                <div style={{ borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', padding: 16 }}>
 <div style={{ fontSize: 12, fontWeight: 600, color: '#a78bfa', marginBottom: 8 }}><FileEdit className="w-4 h-4 inline-block" />생성 프롬프트</div>
                  <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.6, wordBreak: 'break-all' }}>{result.prompt}</div>
                </div>
              )}

              {/* 텍스트 응답 */}
              {result.textResponse && (
                <div style={{ borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', padding: 16 }}>
 <div style={{ fontSize: 12, fontWeight: 600, color: '#a78bfa', marginBottom: 8 }}><MessageSquare className="w-4 h-4 inline-block" />AI 코멘트</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{result.textResponse}</div>
                </div>
              )}
            </div>
          )}

          {/* 초기 상태 */}
          {!loading && !result && (
            <div style={{ height: '100%', minHeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 16, border: '1px solid rgba(139,92,246,0.1)', background: 'rgba(139,92,246,0.02)' }}>
 <div style={{ fontSize: 60, marginBottom: 16, opacity: 0.3 }}><Home className="w-4 h-4 inline-block" /></div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#7c3aed', marginBottom: 8 }}>SketchUp → Photorealistic</div>
              <div style={{ fontSize: 13, color: '#475569', maxWidth: 400, textAlign: 'center', lineHeight: 1.6 }}>
                스케치업 3D 모델 이미지를 업로드하거나<br />NAS에서 선택하면 AI가 실사 렌더링으로 변환합니다
              </div>
              <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
 {[' 와이어프레임', ' 3D 모델', ' 투시도'].map(t =>(
                  <span key={t} style={{ padding: '6px 12px', borderRadius: 20, background: 'rgba(139,92,246,0.08)', color: '#a78bfa', fontSize: 11 }}>{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ fontSize: 9, color: '#7c3aed', fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#e2e8f0' }}>{value}</div>
    </div>
  );
}
